import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Bus, Heart, Users, CheckCircle, ExternalLink } from 'lucide-react';

export const BusOfBlessings = () => {
  const donationLevels = [
    { amount: '$50', title: 'Faith Seed', description: 'Plant a seed of faith in our ministry transportation' },
    { amount: '$100', title: 'Praise Partner', description: 'Join us as a partner in spreading gospel music' },
    { amount: '$500', title: 'Ministry Builder', description: 'Help build the foundation of our mobile ministry' },
    { amount: '$1,000', title: 'Vision Partner', description: 'Share in our vision to serve communities nationwide' },
    { amount: '$2,500', title: 'Covenant Sponsor', description: 'Make a covenant commitment to our ministry expansion' },
    { amount: '$5,000', title: 'Legacy Partner', description: 'Leave a lasting legacy in gospel music ministry' }
  ];

  return (
    <section id="bus-of-blessings" className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <Bus className="w-12 h-12 text-white mr-4" />
            <h2 className="text-4xl md:text-6xl font-bold text-white">
              Help Us Purchase a Ministry Bus!
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            <strong>Nadja Scott & Divine Anointing</strong> are raising <strong>$25,000</strong> to purchase a 
            ministry bus that will help us travel, serve youth and seniors, and support other choirs 
            with safe, affordable transportation!
          </p>
          <p className="text-2xl font-bold text-white mt-4">
            NO AMOUNT TOO SMALL!
          </p>
        </div>

        {/* Bus Image */}
        <div className="mb-16">
          <Card className="max-w-4xl mx-auto overflow-hidden shadow-2xl">
            <CardContent className="p-0">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/2v9ic2x4_IMG_4209.jpeg"
                  alt="Divine Anointing Ministry Bus - Help us reach our $25,000 goal"
                  className="w-full h-auto"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mission Statement */}
        <div className="text-center mb-16">
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">Bus of Blessings Mission</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Users className="w-12 h-12 text-white mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Serve Communities</h4>
                <p className="text-white/90">Reach youth and seniors across the country with gospel music and ministry</p>
              </div>
              <div className="flex flex-col items-center">
                <Bus className="w-12 h-12 text-white mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Safe Transportation</h4>
                <p className="text-white/90">Provide reliable, affordable transportation for choir members and equipment</p>
              </div>
              <div className="flex flex-col items-center">
                <Heart className="w-12 h-12 text-white mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Support Others</h4>
                <p className="text-white/90">Help other choirs and ministries with transportation needs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Donation Levels */}
        <div className="mb-16">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
            Partnership Levels
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {donationLevels.map((level, index) => (
              <Card key={index} className="bg-white/95 hover:bg-white transition-all hover:shadow-xl group">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                    <span className="text-2xl font-bold text-red-600">{level.amount}</span>
                  </div>
                  <h4 className="text-xl font-bold text-black mb-3">{level.title}</h4>
                  <p className="text-black leading-relaxed">{level.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Donation CTA */}
        <div className="text-center mb-12">
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 max-w-2xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Give Here!
            </h3>
            <p className="text-lg text-white/90 mb-8">
              Every donation brings us closer to our goal of spreading the gospel through music across the nation. 
              Your contribution is tax-deductible and makes an eternal impact.
            </p>
            
            <a 
              href="https://divineanointingchoir.square.site/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-white text-red-600 hover:bg-gray-100 px-12 py-4 text-xl font-bold rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105">
                <Heart className="w-6 h-6 mr-3" />
                Donate Now
                <ExternalLink className="w-5 h-5 ml-3" />
              </Button>
            </a>
          </div>
        </div>

        {/* Tax Deductible Notice */}
        <div className="text-center">
          <div className="bg-white/5 rounded-2xl p-6 max-w-2xl mx-auto">
            <p className="text-white text-lg">
              <strong>Healing House Ministries, Inc.</strong> is a 501(c)(3) nonprofit organization.
            </p>
            <p className="text-white text-lg mt-2">
              All donations are tax deductible.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};